import firebase from 'firebase/app';

import TrackerService from './TrackerService';

// Masked so the audit trail (surfaced to admins) never stores a full address/number.
function maskEmail(value) {
  if (!value || typeof value !== 'string' || !value.includes('@')) return value || null;
  const [local, domain] = value.split('@');
  const head = local.slice(0, 2);
  return `${head}${'*'.repeat(Math.max(1, local.length - head.length))}@${domain}`;
}

function maskPhone(value) {
  if (!value || typeof value !== 'string') return value || null;
  return value.length > 4 ? `${'*'.repeat(value.length - 4)}${value.slice(-4)}` : value;
}

/**
 * The credential-event "spine": records every credential change to
 * users/{uid}/credentialEvents/{autoId} and mirrors it to Mixpanel. One record
 * powers the admin change-history list, the Mixpanel event, and counts.
 *
 *   type:   'email' | 'phone' | 'password' | 'account'
 *   action: 'added' | 'removed' | 'verified' | 'changed'
 *         | 'reset_requested' | 'reset_completed' | 'password_changed'
 *         | 'account_closed' | 'account_reopened'
 *   actor:  'user' | 'admin' | 'system'
 *
 * Never throws into the caller — auditing must not break the action it records.
 */
export async function writeCredentialEvent({ uid, type, action, value = null, actor = 'user', meta = {} }) {
  try {
    if (!uid || !type || !action) return;
    const actorUid = firebase.auth().currentUser ? firebase.auth().currentUser.uid : null;
    const maskedValue = type === 'email'
      ? maskEmail(value)
      : type === 'phone'
        ? maskPhone(value)
        : null; // never store password / account values
    const event = {
      type,
      action,
      value: maskedValue,
      actor,
      actorUid,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      meta: meta || {},
    };
    await firebase.firestore()
      .collection('users').doc(uid)
      .collection('credentialEvents').add(event);

    try {
      const tracker = new TrackerService({});
      tracker.mixpanelTrack('credential_event', {
        distinct_id: uid, type, action, actor, ...(meta || {}),
      });
    } catch (mpErr) {
      console.warn('[credentialEvents] mixpanel failed:', mpErr && mpErr.message);
    }
  } catch (err) {
    console.warn('[credentialEvents] write failed:', err && err.message);
  }
}
